import { defineComponent as _defineComponent } from 'vue'
import { computed } from 'vue';
import { useMounted } from '@vueuse/core';

import { useLanguageStore, useUserStore } from '@/helpers/composables/useStores';
import { useArbitrage } from '@/helpers/composables/useArbitrage';

import { usePromotion } from '@/helpers/composables/usePromotion';

import { OfferLocation } from '@/helpers/offer-helper';
import type { Offer } from '@/@types/graphql-types';
import type { SnowplowContext } from '@/helpers/tracking/providers';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import type { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import FreeTrialSwiperOS from '@/features/buybox/components/grid/FreeTrialSwiperOS.vue';
import { freeTrialSwiperRules } from '@/helpers/promotion-helper';
import { useSingleQTV } from '@/features/buybox/composables/useSingleQTV';

import { useActivateExperiment, useExperiment } from '@/libraries/experiment';
import { useTitleDetailInfo } from '@/pages/titleDetailV1/v1/composables/useTitleDetailInfo';
import { usePPExperiments } from '@/components/experiments/PPExperiments';
import { qtvProviders, qtvProviderType } from '@/features/buybox/helpers/quality-tv-data';


export default /*#__PURE__*/_defineComponent({
  __name: 'PromotionPlacement',
  props: {
    offers: null,
    showFreeTrial: { type: Boolean },
    isFreeBuyBox: { type: Boolean },
    title: null,
    additionalContexts: null,
    trackingContexts: null,
    seasonNumber: null,
    episodeNumber: null
  },
  emits: ["clickOut"],
  setup(__props: any, { emit }: { emit: ({
	(
		e: 'clickOut',
		payload: {
			buyboxOffer: TitleOfferFragment | null;
			offerLocation: OfferLocation.FREE_TRIAL;
			row: HTMLElement | null;
		}
	): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
	offers: Offer[];
	showFreeTrial: boolean;
	isFreeBuyBox: boolean;
	title: TitleDetail;
	additionalContexts: SnowplowContext[];
	trackingContexts: SnowplowContext[];
	seasonNumber?: number | null;
	episodeNumber?: number | null;
};

const ListPromotionRow = () => import('@/features/buybox/components/list/ListPromotionRow.vue');
const QualityTV = () => import('@/features/buybox/components/list/QualityTV.vue');
const SingleQTV = () => import('@/features/buybox/components/list/SingleQTV.vue');



const { isProUser } = useUserStore();
const { isArbitrating } = useArbitrage();
const { country } = useLanguageStore();
const isMounted = useMounted();



const { activeVariantWithControlGroup: intOffersRevenueCheckVariant } = useExperiment(
	'INT_OFFERS_REVENUE_CHECK_EXP_V3'
);

// OVER_PROVIDER_EXP_V2
const { activeVariantWithControlGroup: overProviderExpVariant } = useExperiment('OVER_PROVIDER_EXP_V3');
// OVER_PROVIDER_EXP_V2

const { promotionOffer, catalogueItem } = usePromotion({
	title: () => props.title,
	offers: () => props.offers,
});

const qtvAdditionalContexts = computed(() => props.additionalContexts.concat(props.trackingContexts));

// PP Experiments - active them all here
const activatePPExperiments = computed(
	() => [9, 119, 283].includes(promotionOffer.value?.package.packageId || 0) && !isArbitrating.value
);

const { activeVariantWithControlGroup: ppFreeTrialExpVariant } = useActivateExperiment(
	'PP_FREETRIAL_EXP_2',
	activatePPExperiments
);

const { ppExperimentProvider: ppFTExpProvider } = usePPExperiments('ft');

// PP Experiments

const showPromotionRowWhenArbitrating = computed(
	() => isArbitrating.value && 'atp' === promotionOffer.value?.package?.shortName
);

// MGM Promoted Title
const { titleId } = useTitleDetailInfo(() => props.title);
const isMGMPromotedTitle = computed(() => country.value === 'GB' && titleId.value === 'ts361169');

const freeTrialActive = computed(() => {
	if (isMGMPromotedTitle.value) return false;

	if (ppFreeTrialExpVariant.value) return true;

	return (
		!isProUser.value &&
		(catalogueItem.value?.placement === 'ft' || (!catalogueItem.value?.placement && props.showFreeTrial))
	);
});

const hideTVODQTV = computed(
	() =>
		catalogueItem.value?.placement === 'qtv' &&
		!['FREE', 'JWTV'].includes(qtvProviderType[catalogueItem.value.shortName]) &&
		props.isFreeBuyBox
);

const isSingleQTVActive = computed(() => {
	if (isMGMPromotedTitle.value) return false;

	if (isProUser.value || ppFreeTrialExpVariant.value) return false;

	return catalogueItem.value?.placement === 'sqtv';
});

const sqtvProviderShortName = computed(() => {
	if (['variant_2'].includes(intOffersRevenueCheckVariant.value || '')) {
		return 'amp';
	}

	return catalogueItem.value?.shortName || null;
});

const { isSQTVEmpty } = useSingleQTV(() => props.title, sqtvProviderShortName, isSingleQTVActive);

const qualityTvActive = computed(() => {
	if (isMGMPromotedTitle.value) return false;

	if (ppFreeTrialExpVariant.value) return false;
	return (
		!isProUser.value &&
		(!isMounted.value || !showPromotionRowWhenArbitrating.value) &&
		// get qtv placement from config
		catalogueItem.value?.placement === 'qtv' &&
		qtvProviders.includes(catalogueItem.value.shortName) &&
		// Itunes TVOD QTV is not a Free Trial, cannot be shown when filter is Free.
		!hideTVODQTV.value
	);
});

const freeTrialSwiperActive = computed(() => {
	if (!freeTrialSwiperRules || ppFreeTrialExpVariant.value) return false;

	// Currently config is being overridden on 'atp' titles from promotion-helper.ts
	// Keeping this behaviour active when introducing the geoSwiper for Prime
	// this should be handled dynamically in the future
	if (catalogueItem.value?.shortName === 'atp') return false;

	return freeTrialSwiperRules[country.value] != null;
});

function onClickOut(payload: {
	buyboxOffer: TitleOfferFragment | null;
	offerLocation: OfferLocation.FREE_TRIAL;
	row: HTMLElement | null;
}) {
	emit('clickOut', {
		buyboxOffer: payload.buyboxOffer,
		offerLocation: payload.offerLocation,
		row: payload.row,
	});
}

return { __sfc: true,ListPromotionRow, QualityTV, SingleQTV, props, isProUser, isArbitrating, country, isMounted, emit, intOffersRevenueCheckVariant, overProviderExpVariant, promotionOffer, catalogueItem, qtvAdditionalContexts, activatePPExperiments, ppFreeTrialExpVariant, ppFTExpProvider, showPromotionRowWhenArbitrating, titleId, isMGMPromotedTitle, freeTrialActive, hideTVODQTV, isSingleQTVActive, sqtvProviderShortName, isSQTVEmpty, qualityTvActive, freeTrialSwiperActive, onClickOut, FreeTrialSwiperOS }
}

})